import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { QuizAttempt } from '../types/quiz'

import { api } from '@/shared/services/api'

const attemptKey = (id: number | null) => ['/user/quiz_attempts', id]

/**
 * Iniciar é idempotente do lado do aluno: se já existe tentativa em andamento,
 * o backend devolve a MESMA, com o mesmo sorteio. Não há caminho que
 * re-sorteie, então clicar duas vezes não troca as perguntas.
 */
export function useStartQuizAttempt(quizId: number) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ record: QuizAttempt }>(
        `/user/quizzes/${quizId}/quiz_attempts`
      )
      return data.record
    },
    onSuccess: attempt => {
      queryClient.setQueryData(attemptKey(attempt.id), attempt)
    }
  })
}

export function useQuizAttempt(attemptId: number | null) {
  return useQuery({
    queryKey: attemptKey(attemptId),
    queryFn: async () => {
      const { data } = await api.get<{ record: QuizAttempt }>(
        `/user/quiz_attempts/${attemptId}`
      )
      return data.record
    },
    enabled: attemptId !== null
  })
}

export function useAnswerQuizQuestion(attemptId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: {
      quizQuestionId: number
      quizQuestionOptionId: number
    }) => {
      const { data } = await api.post<{ record: QuizAttempt }>(
        `/user/quiz_attempts/${attemptId}/answer`,
        {
          record: {
            quiz_question_id: payload.quizQuestionId,
            quiz_question_option_id: payload.quizQuestionOptionId
          }
        }
      )
      return data.record
    },
    onSuccess: attempt => {
      queryClient.setQueryData(attemptKey(attempt.id), attempt)
    }
  })
}

export function useFinishQuizAttempt(attemptId: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ record: QuizAttempt }>(
        `/user/quiz_attempts/${attemptId}/finish`
      )
      return data.record
    },
    onSuccess: attempt => {
      queryClient.setQueryData(attemptKey(attempt.id), attempt)
      // A conclusão credita pontos/moedas e gera atendimento — o progresso
      // global precisa refletir isso na mesma navegação.
      queryClient.invalidateQueries({ queryKey: ['/user/gamification/progress'] })
      // A listagem E a ficha do quiz: a ficha carrega a pontuação vigente, que
      // acabou de ser substituída por esta tentativa.
      queryClient.invalidateQueries({ queryKey: ['/user/quizzes'] })
      queryClient.invalidateQueries({ queryKey: ['/user/quizzes', attempt.quiz_id] })
    }
  })
}
